/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useAppSelector } from '../../../reducers';
import { MessageFormField, MessageFormProps } from '../types';
import { ContributorVerifierMessageForm } from './ContributorVerifierMessageForm';
import { useGetSurveyUsersQuery } from '@api/initiative-stats';
import { skipToken } from '@reduxjs/toolkit/query';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import { useToggle } from '@hooks/useToggle';
import { DELEGATION_ROLES, hasDelegationRole, hasInheritedOnlyRole, type DelegationRole } from '@utils/user';
import type { SurveyDelegationUser } from '@g17eco/types/user';
import { DELEGATED_HINTS } from '../utils';
import type { UserSelectUser } from './UserSelectField';
import { SystemUsersToggle } from './SystemUsersToggle';
import { NoExplicitUsersNote } from './NoExplicitUsersNote';

interface SurveyMessageFormProps extends MessageFormProps {
  surveyId?: string;
}

type RoleUser = UserSelectUser & { isSystem: boolean };

const groupByRole = (users: SurveyDelegationUser[]) =>
  users.reduce<Record<DelegationRole, RoleUser[]>>(
    (acc, user) => {
      for (const role of DELEGATION_ROLES) {
        if (hasDelegationRole(user.delegationRoles, role)) {
          const isSystem = hasInheritedOnlyRole(user.delegationRoles, role);
          acc[role].push({ ...user, isSystem, hint: isSystem ? DELEGATED_HINTS[role] : undefined });
        }
      }
      return acc;
    },
    { contributor: [], verifier: [] },
  );

const isExplicit = (user: RoleUser) => !user.isSystem;

const keepExplicit = (userIds: string[], users: RoleUser[]) =>
  userIds.filter((id) => users.some((user) => user._id === id && isExplicit(user)));

const SurveyUsersMessageForm = (props: SurveyMessageFormProps & { users: SurveyDelegationUser[] }) => {
  const { users, form, onChange } = props;
  const [showSystemUsers, toggleShowSystemUsers] = useToggle(false);
  const all = groupByRole(users);
  const contributors = showSystemUsers ? all.contributor : all.contributor.filter(isExplicit);
  const verifiers = showSystemUsers ? all.verifier : all.verifier.filter(isExplicit);

  const onToggleSystemUsers = () => {
    toggleShowSystemUsers();
    if (showSystemUsers) {
      // Hidden system users must not stay selected as recipients
      onChange(MessageFormField.ContributorIds)(keepExplicit(form.contributorIds, all.contributor));
      onChange(MessageFormField.VerifierIds)(keepExplicit(form.verifierIds, all.verifier));
    }
  };

  const onlySystemUsersMatch = !showSystemUsers && contributors.length === 0 && verifiers.length === 0;

  const toActions = (
    <>
      {onlySystemUsersMatch ? <NoExplicitUsersNote className='ms-2 mb-2' /> : null}
      <div className='ms-auto'>
        <SystemUsersToggle checked={showSystemUsers} onChange={onToggleSystemUsers} className='mb-2' />
      </div>
    </>
  );

  return <ContributorVerifierMessageForm {...props} contributors={contributors} verifiers={verifiers} toActions={toActions} />;
};

export const SurveyMessageForm = (props: SurveyMessageFormProps) => {
  const { surveyId } = props;

  const initiativeId = useAppSelector(state => state.initiative.loaded ? state.initiative.data._id : undefined);
  const surveyUsers = useGetSurveyUsersQuery(initiativeId && surveyId ? { initiativeId, surveyId } : skipToken);

  return (
    <QueryWrapper
      query={surveyUsers}
      onSuccess={(users) => <SurveyUsersMessageForm {...props} users={users} />}
    />
  );
}
